import { Outlet, useParams } from 'react-router'
import {
  useCourseDetails,
  useAttemptQuestions,
  CourseDetails,
  AttemptQuestionGroup,
} from './api'
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react'
import classes from './QuizAttemptLayout.module.css'

interface QuizProgress {
  attemptId: number
  quizId: number
  currentGroupId: number | null
  currentQuestionIndex: number
  answeredQuestions: number[]
  lastUpdated: string
}

interface QuizContextType {
  courseDetails: CourseDetails
  attemptId: number
  progress: QuizProgress
  updateProgress: (updates: Partial<QuizProgress>) => void
  timerEnabled: boolean
  remainingSeconds: number | null
  questionGroups: AttemptQuestionGroup[]
}

const QuizContext = createContext<QuizContextType | null>(null)

export function useQuizContext() {
  const context = useContext(QuizContext)
  if (!context) {
    throw new Error('useQuizContext must be used within QuizAttemptLayout')
  }
  return context
}

export default function QuizAttemptLayout() {
  const { courseId, attemptId } = useParams<{
    courseId: string
    attemptId: string
  }>()

  const { data: courseDetails } = useCourseDetails({
    course_id: Number(courseId),
  })

  const { data: attemptQuestions } = useAttemptQuestions(Number(attemptId))

  const [progress, setProgress] = useState<QuizProgress>({
    attemptId: Number(attemptId),
    quizId: Number(courseId),
    currentGroupId: null,
    currentQuestionIndex: 0,
    answeredQuestions: [],
    lastUpdated: new Date().toISOString(),
  })

  const updateProgress = (updates: Partial<QuizProgress>) => {
    setProgress((prev) => ({
      ...prev,
      ...updates,
      lastUpdated: new Date().toISOString(),
    }))
  }

  const timerEnabled = Boolean(
    courseDetails.duration_seconds && courseDetails.in_progress_attempt
  )

  const calculateRemainingSeconds = useCallback(() => {
    if (!courseDetails.duration_seconds || !courseDetails.in_progress_attempt) {
      return null
    }
    const startTime = new Date(
      courseDetails.in_progress_attempt.created_at
    ).getTime()
    const elapsed = Math.floor((Date.now() - startTime) / 1000)
    return Math.max(0, courseDetails.duration_seconds - elapsed)
  }, [courseDetails.duration_seconds, courseDetails.in_progress_attempt])

  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(
    calculateRemainingSeconds
  )

  useEffect(() => {
    if (!timerEnabled) {
      return
    }

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const actualRemaining = calculateRemainingSeconds()
        setRemainingSeconds(actualRemaining)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    const interval = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev === null || prev <= 0) return prev
        return prev - 1
      })
    }, 1000)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [timerEnabled, calculateRemainingSeconds])

  const contextValue: QuizContextType = {
    courseDetails,
    attemptId: Number(attemptId),
    progress,
    updateProgress,
    timerEnabled,
    remainingSeconds,
    questionGroups: attemptQuestions.question_groups,
  }

  return (
    <QuizContext.Provider value={contextValue}>
      <div className={classes.container}>
        <Outlet />
      </div>
    </QuizContext.Provider>
  )
}
